import type { RowData } from "@tanstack/react-table";
import { resolveRecordPresentationModes } from "./define-record-object";
import {
  availablePresentationModes,
  type PresentationRenderers,
} from "./presentation-renderers";
import type {
  RecordObjectDef,
  RecordPresentationMode,
  RecordTableProps,
  RecordTableState,
} from "./types";

export function resolveRecordPresentation<TData extends RowData>(
  object: RecordObjectDef<TData>,
  requestedMode: RecordPresentationMode,
  renderers: PresentationRenderers<TData>,
) {
  const modes = availablePresentationModes(
    resolveRecordPresentationModes(object.presentationModes),
    renderers,
  );
  const mode: RecordPresentationMode = modes.includes(requestedMode)
    ? requestedMode
    : "table";
  return { modes, mode, showSwitcher: modes.length > 1 };
}

export function hasActiveRecordFilters(state: RecordTableState): boolean {
  return state.filter.conditions.length > 0 || state.search !== "";
}

export function shouldShowRecordHeader<TData extends RowData>({
  object,
  title,
  primaryAction,
  headerActions,
}: RecordTableProps<TData>): boolean {
  return (
    object.capabilities.switcher ||
    object.capabilities.views ||
    [title, primaryAction, headerActions].some(Boolean)
  );
}

export function shouldShowRecordControls<TData extends RowData>(
  {
    object,
    state,
    controlActions,
    showControls = true,
  }: RecordTableProps<TData>,
  showPresentationSwitcher: boolean,
  canPersistViews: boolean,
): boolean {
  return (
    showControls &&
    Boolean(
      object.capabilities.search ||
      object.quickFilterFields.length > 0 ||
      object.pinnedFilterFields.length > 0 ||
      object.capabilities.advancedFilters ||
      object.capabilities.columnEditing ||
      controlActions ||
      showPresentationSwitcher ||
      (canPersistViews && state.isDirty),
    )
  );
}

export function selectedRowIds(
  rowSelection: Record<string, boolean>,
): string[] {
  return Object.keys(rowSelection).filter((id) => rowSelection[id]);
}
